import { useEffect, useState } from 'react'

import { Button } from '@wartek-id/button-v1'
import { Text } from '@wartek-id/text-v1'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogOverlay,
} from '@wartek-id/dialog-v1'
import type { OptionItem } from '@wartek-id/select'
import { CustomSelect } from '@wartek-id/select'

import {
  listOfPangkat,
  isValidPangkat,
  getPangkatGolongan,
} from 'app/KinerjaPage/utils/pangkat'
import {
  listOfJenjang,
  isValidJenjang,
  mapJenjangName,
} from 'app/KinerjaPage/utils/jenjang'
import { capitalizeEachWords } from 'utils/text'
import {
  DescriptionLabel,
  DescriptionList,
  DescriptionValue,
} from 'app/KinerjaShared/components/DescriptionList'

import type { KinerjaProfil } from 'types/kinerja'

interface EditDialogProps {
  profile: KinerjaProfil
  isOpen?: boolean
  setOpen?: (open: boolean) => void
  onSave?: (profile: KinerjaProfil) => void
}

export default function EditDialog({
  profile,
  isOpen,
  setOpen = () => {},
  onSave = () => {},
}: EditDialogProps) {
  const [profileData, setProfileData] = useState<KinerjaProfil>({ ...profile })

  const changeProfileData = (key: string, value: string) => {
    setProfileData((prev) => ({ ...prev, [key]: value }))
  }

  const [selectedPangkatGolongan, setSelectedPangkatGolongan] =
    useState<OptionItem>({
      name: isValidPangkat(profileData.pangkatGolongan)
        ? getPangkatGolongan(profileData.pangkatGolongan.toUpperCase())
        : 'Pilih pangkat',
      value: isValidPangkat(profileData.pangkatGolongan)
        ? profileData.pangkatGolongan.toUpperCase()
        : '',
      disabled: !isValidPangkat(profileData.pangkatGolongan),
    })

  const [selectedJenjang, setSelectedJenjang] = useState<OptionItem>({
    name: isValidJenjang(profileData.jenjang)
      ? mapJenjangName(profileData.jenjang)
      : 'Pilih jenjang',
    value: isValidJenjang(profileData.jenjang)
      ? capitalizeEachWords(profileData.jenjang)
      : '',
    disabled: !isValidJenjang(profileData.jenjang),
  })

  const jenjangOption = listOfJenjang.filter((jenjang) => {
    if (profileData.kategoriPegawai === 'PPPK') {
      return jenjang !== 'Bawah Pertama'
    }

    return true
  })

  const isValid =
    isValidJenjang(selectedJenjang.value) &&
    (profileData.kategoriPegawai !== 'PNS' ||
      isValidPangkat(selectedPangkatGolongan.value))

  const userType = profileData.kategoriPegawai

  useEffect(() => {
    changeProfileData('pangkatGolongan', selectedPangkatGolongan.value)
  }, [selectedPangkatGolongan])

  useEffect(() => {
    changeProfileData('jenjang', selectedJenjang.value)
  }, [selectedJenjang])

  const handleSave = () => {
    setOpen(false)
    onSave(profileData)
  }

  return (
    <Dialog isOpen={isOpen} onClose={setOpen} size="md">
      <DialogOverlay />
      <DialogContent aria-labelledby="edit-dialog-title">
        <div className="py-10 px-9 mb-2">
          <Text variant="display-sm" id="edit-dialog-title" className="mb-4">
            Edit Profil
          </Text>

          <div className="p-4 rounded bg-surface-warning-subdued mb-6">
            <Text as="div" variant="body">
              <Text variant="body-bold">Catatan:</Text>
              <ul className="list-disc list-outside pl-[18px]">
                <li>
                  Pembaruan data di sini tidak akan mengubah data di Dapodik dan
                  hanya untuk keperluan Pengelolaan Kinerja
                </li>
                <li>
                  Silakan hubungi operator sekolah untuk pembaruan data diri
                  Anda di Dapodik
                </li>
              </ul>
            </Text>
          </div>

          <DescriptionList labelledById="title" className="space-y-6">
            <DescriptionListItem label="Nama" value={profileData.nama || '-'} />
            {userType !== 'UNDEFINED' && (
              <DescriptionListItem label="NIP" value={profileData.nip || '-'} />
            )}
            <DescriptionListItem
              label="Status Kepegawaian"
              value={profileData.statusPegawai || '-'}
            />

            {userType === 'PNS' && (
              <div className="space-y-1">
                <Text as="label" htmlFor="pangkat" variant="body">
                  Pangkat, Golongan Ruang
                </Text>
                <CustomSelect
                  data-testid="pangkat-golongan-select"
                  value={selectedPangkatGolongan}
                  onChange={(e) => {
                    setSelectedPangkatGolongan(e as OptionItem)
                  }}
                  options={[
                    { name: 'Pilih pangkat', disabled: true, value: '' },
                    ...listOfPangkat.map((pangkat) => ({
                      name: getPangkatGolongan(pangkat),
                      value: pangkat,
                      disabled: false,
                    })),
                  ]}
                />
              </div>
            )}

            {userType !== 'UNDEFINED' && (
              <div className="space-y-1">
                <Text as="label" htmlFor="jenjang" variant="body">
                  Jenjang Jabatan
                </Text>
                <CustomSelect
                  data-testid="jenjang-select"
                  value={selectedJenjang}
                  onChange={(e) => {
                    setSelectedJenjang(e as OptionItem)
                  }}
                  options={[
                    { name: 'Pilih jenjang', disabled: true, value: '' },
                    ...jenjangOption.map((jenjang) => ({
                      name: mapJenjangName(jenjang),
                      value: jenjang,
                      disabled: false,
                    })),
                  ]}
                />
              </div>
            )}

            <DescriptionListItem
              label="Satuan Pendidikan Induk"
              value={profileData.satuanPendidikan || '-'}
            />

            {profile.timKinerja && (
              <DescriptionListItem
                label="Tim Kinerja"
                value={profile.timKinerja || '-'}
              />
            )}

            {profile.pejabatPenilaiKinerja && (
              <DescriptionListItem
                label="Pejabat Penilai Kinerja"
                value={profile.pejabatPenilaiKinerja || '-'}
              />
            )}
          </DescriptionList>
        </div>
        <DialogFooter className="px-6 py-4">
          <Button
            size="lg"
            className="w-[155px] mr-5"
            color="white"
            onClick={() => setOpen(false)}
          >
            Keluar
          </Button>
          <Button
            disabled={!isValid}
            className="w-[155px]"
            size="lg"
            onClick={handleSave}
          >
            Simpan
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

type DescriptionListItemProps = {
  label: string
  value: string
}
function DescriptionListItem({ label, value }: DescriptionListItemProps) {
  const labelId = `description-label-${label
    .replace(/\s+/g, '-')
    .toLowerCase()}`

  return (
    <div className="space-y-1" role="presentation">
      <DescriptionLabel className="w-full max-w-56" id={labelId}>
        {label}
      </DescriptionLabel>
      <DescriptionValue variant="body-bold" labelledById={labelId}>
        {value}
      </DescriptionValue>
    </div>
  )
}
